package ui.app.assign.detail.kindergarten.editor

import androidx.compose.runtime.*
import androidx.compose.ui.graphics.Color
import apps.App
import apps.App.Kindergarten.AudienceType
import common.frontend.api.Io
import org.jetbrains.compose.resources.StringResource
import provider.CommonDisplayProvider
import provider.LocalAccountProvider
import provider.LocalDisplayProvider
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.globalViewModel
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tools.*
import ui.GlobalVM
import ui.app.assign.detail.AppDetailArgs
import ui.app.assign.detail.AppWrapperDevices
import ui.app.assign.detail.kindergarten.AppKindergartenScope


class AppKindergartenEditorScope private constructor(
    val vm: AppKindergartenEditorVM,
    val vmGlobal: GlobalVM,
    val action: (AppKindergartenEditorVM.Action) -> Unit,

    override val args: AppDetailArgs,
    override val nav: Navigation,
    override val login: Io.User,
    override val display: CommonDisplayProvider
) : AppKindergartenScope {

    companion object {
        @Composable
        fun rememberScope(): AppKindergartenEditorScope {
            val nav = LocalNavigation.current
            val account = LocalAccountProvider.current
            val display = LocalDisplayProvider.current
            val args = LocalNavigationArgs.current.rememberArgs(AppDetailArgs::class)
            val vm = nav.viewModel(AppKindergartenEditorVM::class, "${args.companyId}:${args.appId}")
            val vmGlobal = globalViewModel(GlobalVM::class)
            val action = AppKindergartenEditorVM.Action.rememberAction(vm)
            return remember {
                AppKindergartenEditorScope(
                    vm = vm,
                    vmGlobal = vmGlobal,
                    action = action,
                    args = args,
                    nav = nav,
                    login = account.current!!,
                    display = display,
                )
            }
        }
    }

    init {
        vm.init(this)
    }

    class Wrapper(
        override val data: Io.AppDetail,
        override val key: String = data.id.toString(),
        private val changeInternal: MutableState<Boolean> = mutableStateOf(false),
    ) : EFieldList.Entry<Io.AppDetail> {

        val appRef: App.Kindergarten get() = data.data as App.Kindergarten

        val change by changeInternal

        val adapterDevices = AppWrapperDevices(data) {
            makeChange()
        }

        val adapterAudio = WrapperAudioEntry(appRef.content.audio) {
            makeChange()
        }

        val adapterCustomPhotoLarge = WrapperPhotoEntry(appRef.content.imagesCustom.photoLarge) {
            makeChange()
        }

        val adapterCustomPhotoSmall = WrapperPhotoEntry(appRef.content.imagesCustom.photoSmall) {
            makeChange()
        }

        val adapterCustomWireframe = WrapperPhotoEntry(appRef.content.imagesCustom.wireframe) {
            makeChange()
        }


        val name = EFieldTextString(
            property = data::name,
            condition = { it.trim().takeIf { it.length > 4 } },
            onChange = { makeChange() },
            onErr = { makeChange() })

        val icon = EFieldRegularLong(
            property = data::logo,
            onChange = { makeChange() })

        val audienceType = EFieldRegularInt(
            property = appRef.content::audienceType,
            onChange = { makeChange() })

        val themeType = EFieldRegularInt(
            property = appRef.content::themeType,
            onChange = {
                val type = App.Kindergarten.ThemeType[it]
                colorSurface.set(Color(type.defaultColor.surface))
                colorOnSurface.set(Color(type.defaultColor.onSurface))
                backgroundImg.set(type.imageBg)

                appRef.content.bundle = type.bundle

                adapterAudio.removeAll { true }
                adapterCustomWireframe.removeAll { true }
                adapterCustomPhotoSmall.removeAll { true }
                adapterCustomPhotoLarge.removeAll { true }

            })

        val backgroundImg = EFieldRegularLong(
            property = appRef.content::backgroundImg,
            onChange = { makeChange() })

        val colorSurface = EFieldColorAsLong(
            property = appRef.content.colors::surface,
            onChange = { makeChange() })

        val colorOnSurface = EFieldColorAsLong(
            property = appRef.content.colors::onSurface,
            onChange = { makeChange() })

        /* LABELS */
        private val gameNameMemory = EFieldRegularString(
            property = appRef.content.labels::gameNameMemory,
            onChange = { makeChange() })
        private val gameNameSudoku = EFieldRegularString(
            property = appRef.content.labels::gameNameSudoku,
            onChange = { makeChange() })
        private val gameNamePaint = EFieldRegularString(
            property = appRef.content.labels::gameNamePaint,
            onChange = { makeChange() })
        private val gameNameSort = EFieldRegularString(
            property = appRef.content.labels::gameNameSort,
            onChange = { makeChange() })
        private val gameNameWords = EFieldRegularString(
            property = appRef.content.labels::gameNameWords,
            onChange = { makeChange() })
        private val gameNameMerge = EFieldRegularString(
            property = appRef.content.labels::gameNameMerge,
            onChange = { makeChange() })
        private val gameNameOrder = EFieldRegularString(
            property = appRef.content.labels::gameNameOrder,
            onChange = { makeChange() })
        private val gameNameCounts = EFieldRegularString(
            property = appRef.content.labels::gameNameCounts,
            onChange = { makeChange() })
        private val gameNamePuzzle = EFieldRegularString(
            property = appRef.content.labels::gameNamePuzzle,
            onChange = { makeChange() })
        private val alphabet = EFieldRegularString(
            property = appRef.content.labels::alphabet,
            onChange = { makeChange() })

        private val labels = listOf(
            gameNameSudoku,
            gameNameOrder,
            gameNameMerge,
            gameNameCounts,
            gameNameWords,
            gameNamePaint,
            gameNameMemory,
            gameNameSort,
            gameNamePuzzle,
            alphabet
        )
        private val allFields = labels + listOf(
            name,
            icon,
            audienceType,
            themeType,
            backgroundImg,

            colorSurface,
            colorOnSurface
        )

        suspend fun setupLabels() {
            if (labels.any { it.state.isEmpty() }) {
                gameNameSudoku.set(Res.string.layer_bottom_label__kindergarten_sudoku.await())
                gameNameOrder.set(Res.string.layer_bottom_label__kindergarten_order.await())
                gameNameMerge.set(Res.string.layer_bottom_label__kindergarten_merge.await())
                gameNameCounts.set(Res.string.layer_bottom_label__kindergarten_counts.await())
                gameNameWords.set(Res.string.layer_bottom_label__kindergarten_words.await())
                gameNamePaint.set(Res.string.layer_bottom_label__kindergarten_paint.await())
                gameNameMemory.set(Res.string.layer_bottom_label__kindergarten_memory.await())
                gameNameSort.set(Res.string.layer_bottom_label__kindergarten_sort.await())
                gameNamePuzzle.set(Res.string.layer_bottom_label__kindergarten_puzzle.await())
                alphabet.set(Res.string.layer_bottom_label__kindergarten_alphabet.await())
            }
        }

        private fun makeChange() {
            changeInternal.value = allFields.any { it.change }
                    || adapterDevices.change
                    || adapterAudio.change
                    || adapterCustomWireframe.change
                    || adapterCustomPhotoSmall.change
                    || adapterCustomPhotoLarge.change
        }
    }

    class WrapperPhotoEntry(
        items: MutableList<App.Kindergarten.Photo>,
        private var initial: String = "",
        private val changeInternal: MutableState<Boolean> = mutableStateOf(false),
        val onChangeInvoke: () -> Unit,
    ) : EFieldList<App.Kindergarten.Photo, WrapperPhotoEntry.Item>(
        items = items,
        onBindEntry = { data ->
            Item(data) {
                changeInternal.value = state.joinToString { it.toString() } != initial
                onChangeInvoke()
            }
        },
        onChange = {
            changeInternal.value = state.joinToString { it.toString() } != initial
            onChangeInvoke()
        }
    ) {

        override var change by changeInternal

        init {
            applyChanges()
        }

        override fun applyChanges() {
            initial = state.joinToString { it.toString() }
        }

        class Item(
            override val data: App.Kindergarten.Photo,
            override val key: String = data.resource.toString(),
            val onChange: (() -> Unit)
        ) : Entry<App.Kindergarten.Photo> {

            val resource = EFieldRegularLong(
                property = data::resource,
                onChange = { onChange() })

            val colors = EFieldColorAsLong(
                property = data::colorBg,
                onChange = { onChange() })

            override fun toString(): String {
                return "${data.names.joinToString()}||${resource.state}||${colors.state.value}"
            }
        }
    }

    class WrapperAudioEntry(
        items: MutableList<App.Kindergarten.Audio>,
        private var initial: String = "",
        private val changeInternal: MutableState<Boolean> = mutableStateOf(false),
        val onChangeInvoke: () -> Unit,
    ) : EFieldList<App.Kindergarten.Audio, WrapperAudioEntry.Item>(
        items = items,
        onBindEntry = { data ->
            Item(data) {
                changeInternal.value = state.joinToString { it.toString() } != initial
                onChangeInvoke()
            }
        },
        onChange = {
            changeInternal.value = state.joinToString { it.toString() } != initial
            onChangeInvoke()
        }
    ) {

        override var change by changeInternal

        init {
            applyChanges()
        }

        override fun applyChanges() {
            initial = state.joinToString { it.toString() }
        }

        class Item(
            override val data: App.Kindergarten.Audio,
            override val key: String = data.resource.toString(),
            private val onChange: (() -> Unit)
        ) : Entry<App.Kindergarten.Audio> {

            val resource = EFieldRegularLong(
                property = data::resource,
                onChange = { onChange() })

            val name = EFieldTextString(
                property = data::name,
                onChange = { onChange() })

            override fun toString(): String {
                return "${name.state}||${resource.state}"
            }
        }
    }
}

fun AudienceType.icon(): Long = when (this) {
    AudienceType.Large -> 58951300
    AudienceType.Small -> 49475300
}

fun AudienceType.label(): StringResource = when (this) {
    AudienceType.Large -> Res.string.layer_bottom__kindergarten_audience_large
    AudienceType.Small -> Res.string.layer_bottom__kindergarten_audience_small
}

fun App.Kindergarten.ThemeType.icon(): Long = when (this) {
    App.Kindergarten.ThemeType.AnimalsExotic -> 65618300
    App.Kindergarten.ThemeType.AnimalsLocal -> 127041300
    App.Kindergarten.ThemeType.Family -> 46872300
    App.Kindergarten.ThemeType.Food -> 59980300
    App.Kindergarten.ThemeType.GeometricShapes -> 120333300
    App.Kindergarten.ThemeType.Holidays -> 79185300
    App.Kindergarten.ThemeType.Seasons -> 78750300
    App.Kindergarten.ThemeType.Transportation -> 15300
}

fun App.Kindergarten.ThemeType.label(): StringResource = when (this) {
    App.Kindergarten.ThemeType.AnimalsExotic -> Res.string.layer_bottom__kindergarten_theme_animals_exotic
    App.Kindergarten.ThemeType.AnimalsLocal -> Res.string.layer_bottom__kindergarten_theme_animals_local
    App.Kindergarten.ThemeType.Family -> Res.string.layer_bottom__kindergarten_theme_family
    App.Kindergarten.ThemeType.Food -> Res.string.layer_bottom__kindergarten_theme_food
    App.Kindergarten.ThemeType.GeometricShapes -> Res.string.layer_bottom__kindergarten_theme_geometric_shapes
    App.Kindergarten.ThemeType.Holidays -> Res.string.layer_bottom__kindergarten_theme_holidays
    App.Kindergarten.ThemeType.Seasons -> Res.string.layer_bottom__kindergarten_theme_seasons
    App.Kindergarten.ThemeType.Transportation -> Res.string.layer_bottom__kindergarten_theme_transportation
}